import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Question from "./Question";
import questions from "../data/questions";
import isProd from "../config";
import ReactGA from "react-ga4";

const CREATE_MEAL_PLAN_API = isProd
  ? process.env.REACT_APP_CREATE_MEAL_PLAN_API_LIVE
  : process.env.REACT_APP_CREATE_MEAL_PLAN_API_TEST;

function buildQuizSlides(questions) {
  return questions.map((q) => ({ type: "question", question: q })).concat([{ type: "email" }]);
}

const bgMain = "bg-[#F6F5EC]";
const cardBg = "bg-[#FAF9F6]";
const btnTertiary = "bg-[#B7B7A4] text-[#4F5D2F] border-[#B7B7A4]";
const btnPrimary = "bg-[#A3B18A] hover:bg-[#769656] text-white";

const loadingSteps = [
    {
        title: "Why We Ask About You",
        message: "Your age, sex, height, and weight help us tailor nutrition advice and risk assessment to your unique needs. This information is essential for safe, personalized recommendations."
    },
    {
        title: "Understanding Your Preferences",
        message: "We analyze your favorite foods, dietary patterns, and lifestyle to ensure your meal plan is enjoyable and sustainable."
    },
    {
        title: "Considering Your Symptoms & Side Effects",
        message: "We factor in any eating-related symptoms or side effects to recommend meals that are comfortable and safe for you."
    },
    {
        title: "Reviewing Your Medical Treatments",
        message: "Your current or recent cancer treatments and medications are considered to avoid any food-drug interactions and support your recovery."
    },
    {
        title: "Accounting for Allergies & Restrictions",
        message: "We exclude foods based on your allergies and dietary restrictions to keep your plan safe and suitable."
    },
    {
        title: "Adapting to Your Kitchen & Habits",
        message: "We match recipes to your available kitchen equipment and your preferred meal frequency, making cooking easier for you."
    },
    {
        title: "Customizing Fluids & Sugar Intake",
        message: "Fluid needs and sugar preferences are balanced to keep you hydrated and energized."
    },
    {
        title: "Personalizing for Culture & Values",
        message: "Cultural, religious, or personal values are respected to make your plan meaningful and relevant."
    },
    {
        title: "Selecting Delicious Recipes",
        message: "We’re picking recipes that fit your tastes, needs, and cooking setup."
    },
    {
        title: "Balancing Nutrition for Your Needs",
        message: "Your plan is being balanced for protein, fiber, vitamins, and minerals based on your answers."
    },
    {
        title: "Creating Your Grocery List",
        message: "We’re generating a simple, organized grocery list with all the ingredients you’ll need." // [1][2]
    },
    {
        title: "Finalizing Your Meal Plan",
        message: "We’re putting the finishing touches on your personalized meal plan. Almost done!"
    }
  /* ... your loading steps as in your code ... */
];

function PlanSkeleton() {
  return (
    <div className="max-w-lg w-full space-y-4 mb-6">
      <Skeleton height={30} width="60%" />
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} height={20} />
      ))}
    </div>
  );
}

// Convert inches to cm
function inchesToCm(inches) {
  return inches * 2.54;
}

// Convert lbs to kg
function lbsToKg(lbs) {
  return lbs * 0.45359237;
}

export default function Quiz() {
  const location = useLocation();
  const queryString = location.search;
  const { stepId } = useParams();
  const navigate = useNavigate();

  const [answers, setAnswers] = useState({});
  const [unit, setUnit] = useState("metric"); // metric or imperial
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [error, setError] = useState("");
  const [favoriteFoodGroups, setFavoriteFoodGroups] = useState([]);
  // eslint-disable-next-line
  const [favoriteFoodDetails, setFavoriteFoodDetails] = useState({});
  const [generating, setGenerating] = useState(false);
  const [planError, setPlanError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const [progress, setProgress] = useState(1);

  const spinnerTimeout = useRef(null);
  const msgInterval = useRef(null);
  const progressInterval = useRef(null);

  const quizSlides = buildQuizSlides(questions);
  const step = Number(stepId) || 0;
  const slide = quizSlides[step];
  const totalSlides = quizSlides.length;

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });
  }, []);

  useEffect(() => {
    if (step === 0 && slide && slide.type === "question") {
      ReactGA.event("quiz_started");
    }
  }, [step, slide]);

  useEffect(() => {
    if (generating) {
      spinnerTimeout.current = setTimeout(() => setShowSpinner(true), 200);

      msgInterval.current = setInterval(() => {
        setLoadingMsgIndex((i) => (i >= loadingSteps.length - 1 ? i : i + 1));
      }, 10000);

      setProgress(1);
      progressInterval.current = setInterval(() => {
        setProgress((prev) => (prev < 95 ? prev + 1 : 95));
      }, 2526);

      return () => {
        clearTimeout(spinnerTimeout.current);
        clearInterval(msgInterval.current);
        clearInterval(progressInterval.current);
      };
    } else {
      setShowSpinner(false);
      setLoadingMsgIndex(0);
      setProgress(1);
      clearInterval(progressInterval.current);
    }
  }, [generating]);

  function validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  function handleNext(answer) {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });

    setError("");
    setEmailError("");

    if (slide.type === "email") {
      ReactGA.event("quiz_completed");

      if (!validateEmail(email)) {
        setEmailError("Please enter a valid email address.");
        return;
      }
      ReactGA.event("email_submitted");

      setAnswers((prev) => ({ ...prev, email }));
      setGenerating(true);
      setPlanError("");
      fetch(`${CREATE_MEAL_PLAN_API}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userAnswers: { ...answers, email }, numDays: 7, email }),
      })
        .then(async (res) => {
          if (!res.ok) throw new Error("Failed to generate plan. Please try again.");
          const data = await res.json();
          setProgress(100);
          setTimeout(() => {
            setGenerating(false);
            navigate(`/plan/${data.planId}${queryString ? queryString : ""}`, { state: { email } });
          }, 300);
        })
        .catch((err) => {
          setPlanError(err.message || "An error occurred.");
          setGenerating(false);
        });
      return;
    }

    function formatFeetInches(totalInches) {
      const ft = Math.floor(totalInches / 12);
      const inch = Math.round(totalInches % 12);
      return `${ft} ft ${inch} in`;
    }

    if (slide.type === "question") {
      const q = slide.question;

      if (q.type === "food-group-multiselect") {
        setFavoriteFoodGroups(answer);
        setAnswers((prev) => ({ ...prev, [q.id]: answer }));
        navigate(`/quiz/step/${step + 1}${queryString ? queryString : ""}`);
        return;
      }

      if (q.type === "food-group-details") {
        setFavoriteFoodDetails(answer);
        setAnswers((prev) => ({ ...prev, [q.id]: answer }));
        navigate(`/quiz/step/${step + 1}${queryString ? queryString : ""}`);
        return;
      }

      if (q.type === "weight") {
        // Normalize and convert to kg if imperial before validating and submitting
        const rawVal = weight.replace(",", ".");
        const val = Number(rawVal);
        const min = unit === "metric" ? q.minMetric : q.minImperial;
        const max = unit === "metric" ? q.maxMetric : q.maxImperial;
        if (isNaN(val) || val < min || val > max) {
          setError(`Please enter a valid weight (${min}–${max} ${unit === "metric" ? "kg" : "lbs"})`);
          return;
        }
        const valueInKg = unit === "imperial" ? lbsToKg(val) : val;
        setAnswers((prev) => ({ ...prev, [q.id]: valueInKg, [`${q.id}_unit`]: "kg" }));
      } else if (q.type === "height") {
        // Normalize and convert to cm if imperial before validating and submitting
        const rawVal = height.replace(",", ".");
        const val = Number(rawVal);
        const min = unit === "metric" ? q.minMetric : q.minImperial;
        const max = unit === "metric" ? q.maxMetric : q.maxImperial;
        if (isNaN(val) || val < min || val > max) {
          if (unit === "imperial") {
            setError(`Please enter a valid height (${formatFeetInches(min)} – ${formatFeetInches(max)})`);
          } else {
            setError(`Please enter a valid height (${min}–${max} cm)`);
          }
          return;
        }
        const valueInCm = unit === "imperial" ? inchesToCm(val) : val;
        setAnswers((prev) => ({ ...prev, [q.id]: valueInCm, [`${q.id}_unit`]: "cm" }));
      } else if (q.type === "number") {
        const val = Number(answer);
        if (isNaN(val) || val < q.min || val > q.max) {
          setError(`Please enter a valid age (${q.min}–${q.max})`);
          return;
        }
        setAnswers((prev) => ({ ...prev, [q.id]: val }));
      } else {
        setAnswers((prev) => ({ ...prev, [q.id]: answer }));
      }
      let nextStep = step + 1;
      if (q.skipNextIfNo && answer === "No") {
        nextStep += q.skipNextIfNo;
      }
      navigate(`/quiz/step/${nextStep}${queryString ? queryString : ""}`);
    }
  }

  function handleBack() {
    setError("");
    setEmailError("");
    if (step > 0) {
      navigate(`/quiz/step/${step - 1}${queryString ? queryString : ""}`);
    }
  }

  function handleUnitChange(newUnit) {
    setUnit(newUnit);
    setWeight("");
    setHeight("");
    setError("");
  }

  if (generating) {
    return (
      <div className={`${bgMain} min-h-screen flex items-center justify-center`}>
        <div className={`${cardBg} p-8 rounded-xl shadow-lg text-center max-w-md w-full`}>
          <h2 className="text-2xl font-bold mb-4 text-[#769656]">Generating your plan…</h2>
          {planError && <div className="text-red-600 mb-2">{planError}</div>}
          {showSpinner && (
            <div className="flex flex-col items-center w-full mt-8">
              <div className="mb-4">
                <svg
                  className="animate-spin h-8 w-8 text-[#A3B18A]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                </svg>
              </div>
              <div className="mb-2 text-[#4F5D2F] font-bold" style={{ minHeight: 24 }}>
                {loadingSteps[loadingMsgIndex].title}
              </div>
              <div className="mb-2 text-[#4F5D2F] font-medium" style={{ minHeight: 24 }}>
                {loadingSteps[loadingMsgIndex].message}
              </div>
              <div className="w-full max-w-lg mb-2">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-[#A3B18A] h-4 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="text-right text-sm text-[#4F5D2F] mt-1" style={{ minWidth: 40 }}>
                  {progress}%
                </div>
              </div>
              <div className="text-[#8b5d1a] font-medium text-sm bg-yellow-100 border border-yellow-300 rounded-md px-4 py-2 mb-4">
                Just a heads-up: This may take up to <strong>3–5 minutes</strong> as our system runs
                detailed calculations to build your personalized plan.
                <br />
                Thanks for your patience!
              </div>
              <PlanSkeleton />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (slide && slide.type === "email") {
    return (
      <div className={`${bgMain} min-h-screen flex flex-col items-center justify-center p-2`}>
        <div className={`${cardBg} p-6 rounded-xl shadow-lg max-w-md w-full relative`}>
          <h2 className="text-2xl font-bold mb-6 text-center text-[#769656]">
            Please enter your email
          </h2>
          <p className="mb-4 text-center text-gray-700 text-base">
            Enter your email to receive your personalized plan and ongoing updates. We respect your
            privacy and will never share your email.
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full p-3 rounded border border-gray-300 mb-2"
            disabled={generating}
          />
          {emailError && <div className="text-red-600 mb-2">{emailError}</div>}

          <button
            onClick={() => handleNext(email)}
            className={`mt-4 px-6 py-3 rounded-lg font-semibold ${btnPrimary}`}
            disabled={generating}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${bgMain} min-h-screen flex flex-col items-center justify-center p-2`}>
      {/* Back button above the card */}
      <div className="w-full max-w-md flex justify-start mb-2">
        <button
          onClick={handleBack}
          className={`px-4 py-2 rounded-lg font-semibold ${btnTertiary}`}
          disabled={step === 0}
          type="button"
        >
          Back
        </button>
      </div>
      <div className={`${cardBg} p-6 rounded-xl shadow-lg max-w-md w-full relative`}>
        {/* Progress bar and percentage */}
        <div className="w-full mb-4 flex items-center gap-2">
          <div className="flex-1">
            <div className="w-full bg-[#DDE5B6] rounded-full h-2">
              <div
                className="bg-[#A3B18A] h-2 rounded-full transition-all duration-300"
                style={{ width: `${((step + 1) / totalSlides) * 100}%` }}
              ></div>
            </div>
          </div>
          <div className="min-w-[48px] text-right text-[#769656] text-sm font-semibold">
            {Math.round(((step + 1) / totalSlides) * 100)}%
          </div>
        </div>
        {/* Question */}
        {slide && slide.type === "question" && (
          <>
            <div className="mb-2 text-[#769656] text-sm text-center font-semibold uppercase tracking-wide">
              {slide.question.section}
            </div>
            <h2 className="text-2xl font-bold mb-6 text-center text-[#769656]">
              {slide.question.text}
            </h2>
            <Question
              q={slide.question}
              onNext={handleNext}
              onUnitChange={handleUnitChange}
              unit={unit}
              prevValue={answers[slide.question.id]}
              weight={weight}
              setWeight={setWeight}
              height={height}
              setHeight={setHeight}
              error={error}
              favoriteFoodGroups={favoriteFoodGroups}
              setFavoriteFoodDetails={setFavoriteFoodDetails}
            />
          </>
        )}
      </div>
    </div>
  );
}
